/**
 * @crm/design-tokens — Sistema de diseño compartido Web ↔ Móvil.
 *
 * Tres niveles:
 *   1. primitives  → valores crudos (rampas, escalas)
 *   2. semantic    → propósito (`surface`, `danger`, `fgMuted`) por tema
 *   3. domain      → vocabulario del CRM (estado de ticket, prioridad, KPI…)
 *
 * Consumo:
 *   Web    → `@crm/design-tokens/css` (variables CSS) + preset de Tailwind
 *   Móvil  → `@crm/design-tokens/native` (objeto de tema para RN) + NativeWind
 *   Ambos  → este índice para lógica (helpers de tono, metas de SLA, etc.)
 */

export * from './primitives';
export * from './semantic';
export * from './domain';
export * from './helpers';

import * as primitives from './primitives';
import { colorSchemes, chartCategorical, chartSequential, chartDiverging, chartSupport } from './semantic';
import { domainTokens } from './domain';
import type { ChartSupport, ColorScheme, SemanticColors } from './semantic';

export interface Theme {
  scheme: ColorScheme;
  colors: SemanticColors;
  chart: {
    categorical: readonly string[];
    sequential: readonly string[];
    diverging: readonly string[];
    support: ChartSupport;
  };
  domain: typeof domainTokens;
  space: typeof primitives.space;
  radius: typeof primitives.radius;
  fontSize: typeof primitives.fontSize;
  fontWeight: typeof primitives.fontWeight;
  fontFamily: typeof primitives.fontFamily;
  nativeFontFamily: typeof primitives.nativeFontFamily;
  typeRole: typeof primitives.typeRole;
  shadow: typeof primitives.shadow;
  nativeShadow: typeof primitives.nativeShadow;
  duration: typeof primitives.duration;
  easing: typeof primitives.easing;
  easingBezier: typeof primitives.easingBezier;
  spring: typeof primitives.spring;
  breakpoint: typeof primitives.breakpoint;
  zIndex: typeof primitives.zIndex;
  controlHeight: typeof primitives.controlHeight;
  size: typeof primitives.size;
  opacity: typeof primitives.opacity;
  borderWidth: typeof primitives.borderWidth;
}

export function buildTheme(scheme: ColorScheme): Theme {
  return {
    scheme,
    colors: colorSchemes[scheme],
    chart: {
      categorical: chartCategorical[scheme],
      sequential: chartSequential[scheme],
      diverging: chartDiverging[scheme],
      support: chartSupport[scheme],
    },
    domain: domainTokens,
    space: primitives.space,
    radius: primitives.radius,
    fontSize: primitives.fontSize,
    fontWeight: primitives.fontWeight,
    fontFamily: primitives.fontFamily,
    nativeFontFamily: primitives.nativeFontFamily,
    typeRole: primitives.typeRole,
    shadow: primitives.shadow,
    nativeShadow: primitives.nativeShadow,
    duration: primitives.duration,
    easing: primitives.easing,
    easingBezier: primitives.easingBezier,
    spring: primitives.spring,
    breakpoint: primitives.breakpoint,
    zIndex: primitives.zIndex,
    controlHeight: primitives.controlHeight,
    size: primitives.size,
    opacity: primitives.opacity,
    borderWidth: primitives.borderWidth,
  };
}

export const lightTheme = buildTheme('light');
export const darkTheme = buildTheme('dark');
export const themes = { light: lightTheme, dark: darkTheme } as const;
